<script setup>
import { useGetAgeLimitString } from '~/core/composables/useGetAgeLimitString'

const props = defineProps({
  selectedCustomer: {
    type: Number,
  },
  selectedCustomerAge: {
    type: Object,
  },
  event: {
    type: Object,
  },
})

const { t } = useI18n()
const { isPeloton } = useEnterpriseClient()
const { getAgeLimitFilterString } = useGetAgeLimitString()

const hasAgeValue = computed(() => !!props.selectedCustomerAge)

const ageLimitStringOptions = computed(() => {
  return {
    max: props.event?.offering_type?.max_allowed_age,
    min: props.event?.offering_type?.min_allowed_age,
  }
})
</script>

<template>
  <div class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap">
    <DSAlert
      type="danger"
      no-close
      class="w-100%"
      aria-live="assertive"
    >
      <template v-if="isPeloton">
        <i18n-t
          v-if="hasAgeValue"
          tag="p"
          keypath="We are unable to process your reservation. Class Participants are required to be at least {years} years old."
        >
          <template #years>
            <span>{{ ageLimitStringOptions.min }}</span>
          </template>
        </i18n-t>
        <i18n-t
          v-else
          tag="p"
          keypath="In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs."
        >
          <template #profile>
            <BaseLink
              :url="`/account/customers/${selectedCustomer}`"
              :v2-link="true"
              :apply-current-provider-prefix="true"
              class="bold fw-600 underline"
            >
              {{ t('profile') }}
            </BaseLink>
          </template>
        </i18n-t>
      </template>
      <template v-else>
        <i18n-t
          tag="p"
          keypath="You cannot join this class because the age restriction is {years} years."
        >
          <template #years>
            <span>{{ getAgeLimitFilterString(ageLimitStringOptions) }}</span>
          </template>
        </i18n-t>
        <i18n-t
          v-if="!hasAgeValue"
          tag="p"
          class="mt-4"
          keypath="In order to register for this class you need to add your Date of Birth to {profile}."
        >
          <template #profile>
            <BaseLink
              :url="`/account/customers/${selectedCustomer}`"
              :v2-link="true"
              :apply-current-provider-prefix="true"
              class="bold fw-600 underline"
            >
              {{ t('your profile') }}
            </BaseLink>
          </template>
        </i18n-t>
      </template>
    </DSAlert>
  </div>
</template>

<i18n>
{
  "en-us": {
    "You cannot join this class because the age restriction is {years} years.": "You cannot join this class because the age restriction is {years} years.",
    "In order to register for this class you need to add your Date of Birth to {profile}.": "In order to register for this class you need to add your Date of Birth to {profile}.",
    "your profile": "your profile",
    "profile": "profile",
    "We are unable to process your reservation. Class Participants are required to be at least {years} years old.": "We are unable to process your reservation. Class Participants are required to be at least {years} years old.",
    "In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs.": "In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs."
  }
}
</i18n>


<i18n locale="de" lang="json">
{
  "In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs.": "Bitte füge deinem {profile} dein Geburtsdatum hinzu, um dich für diesen Kurs anzumelden. Weitere Informationen zu den Registrierungsbedingungen findest du in unseren häufig gestellten Fragen (FAQs).",
  "In order to register for this class you need to add your Date of Birth to {profile}.": "Um sich für diesen Kurs anzumelden, müssen Sie Ihr Geburtsdatum zu Ihrem {profile} hinzufügen.",
  "We are unable to process your reservation. Class Participants are required to be at least {years} years old.": "Wir konnten deine Buchung leider nicht bearbeiten. Kursteilnehmer müssen mindestens {years} Jahre alt sein.",
  "You cannot join this class because the age restriction is {years} years.": "Sie können an dieser Klasse nicht teilnehmen, da die Altersbeschränkung {years} Jahre beträgt.",
  "your profile": "dein Profil"
}
</i18n>

<i18n locale="es" lang="json">
{
  "In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs.": "Para inscribirte en esta clase, añade la fecha de nacimiento en tu {profile}. Para más detalles sobre los requisitos de registro, consulta nuestras Preguntas frecuentes.",
  "In order to register for this class you need to add your Date of Birth to {profile}.": "Para inscribirse en esta clase debe añadir su fecha de nacimiento a su {profile}.",
  "We are unable to process your reservation. Class Participants are required to be at least {years} years old.": "No podemos procesar tu reserva. Los participantes de la clase deben tener al menos {years} años.",
  "You cannot join this class because the age restriction is {years} years.": "No puedes unirte a esta clase porque la restricción de edad es de {years} años.",
  "your profile": "tu perfil"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "In order to register for this class please add your date of birth to your {profile}. For more details on registration requirements please see our FAQs.": "Pour vous inscrire à ce cours, veuillez ajouter votre date de naissance à votre {profile}. Pour plus de détails sur les conditions d'inscription, consultez notre FAQ.",
  "In order to register for this class you need to add your Date of Birth to {profile}.": "Pour vous inscrire à ce cours, vous devez ajouter votre date de naissance à votre {profile}.",
  "We are unable to process your reservation. Class Participants are required to be at least {years} years old.": "Nous ne pouvons pas traiter votre réservation. Les participants au cours doivent être âgés d'au moins {years} ans.",
  "You cannot join this class because the age restriction is {years} years.": "Vous ne pouvez pas rejoindre ce cours car la restriction d'âge est de {years} ans.",
  "your profile": "votre profil"
}
</i18n>